/**
 * File-attachment API for the content-review thread — the server's file
 * policy (what may be attached), the one endpoint every attached file
 * (picked or captured) uploads through, and the matching raw-bytes fetch a
 * viewer reads back.
 *
 * Shares its REST prefix with campaignQueries.ts's other content-thread
 * endpoints (notes, messages) via the same exported `contentThreadPath`
 * builder, so the path segment is built in exactly one place.
 */
import { apiClient, getAuthToken, isNotFoundError } from '@discovr/common';
import type { DataResponse, ThreadFilePolicy, ThreadFileView } from '@discovr/types';

import { contentThreadPath } from './campaignQueries';

/** The slowest link an upload must still get through on. */
const MIN_THREAD_FILE_BYTES_PER_SECOND = 64 * 1024;
/** Headroom over the pure transfer time: connection setup and the server's own work. */
const THREAD_FILE_TRANSFER_SLACK_MS = 30_000;
const MS_PER_SECOND = 1000;

/**
 * A download's size isn't known until its bytes arrive, so every download
 * gets this one budget — past it, the viewer offers a retry.
 */
export const THREAD_FILE_DOWNLOAD_TIMEOUT_MS = 3 * 60_000;

/**
 * How long uploading `bytes` may take: the transfer at the minimum
 * throughput, plus slack. Sized to the file actually sent, so it follows
 * whatever the server's policy lets through — apiClient's default timeout
 * cuts off a large file on a slow or shared uplink, and every retry restarts
 * from byte 0.
 */
export function uploadTimeoutMs(bytes: number): number {
  return (bytes / MIN_THREAD_FILE_BYTES_PER_SECOND) * MS_PER_SECOND + THREAD_FILE_TRANSFER_SLACK_MS;
}

/** The shape axios reports upload progress with. Declared locally rather
 *  than imported from `axios` — this package depends on `apiClient` (from
 *  @discovr/common), not on axios itself, and importing its types here
 *  would be a phantom dependency. */
interface UploadProgressEvent {
  loaded: number;
  total?: number;
}

export interface UploadThreadFileArgs {
  entryId: string;
  file: Blob;
  name: string;
  /** Fraction (0..1) of bytes sent so far. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** Builds the multipart body: the file under its picked/captured `name`.
 *  The server reads an image's dimensions from its bytes, never from the client. */
function buildUploadForm({ file, name }: UploadThreadFileArgs): FormData {
  const form = new FormData();
  form.append('file', file, name);
  return form;
}

/** Turns a 0..1 progress callback into the shape apiClient's
 *  `onUploadProgress` config expects — 0 while the total byte count isn't
 *  known yet (matches importBatchMutations.ts's multipart pattern). */
function toUploadProgressHandler(
  onProgress: UploadThreadFileArgs['onProgress'],
): (event: UploadProgressEvent) => void {
  return (event) => {
    onProgress?.(event.total ? event.loaded / event.total : 0);
  };
}

/**
 * GET /api/v1/roster/{entry_id}/threads/content/file-policy
 *
 * The server's upload rules for this thread: the types that may be attached,
 * the per-file and per-frame byte limits, and how many files one message may
 * carry. The composer checks picks and captured frames against these — the
 * client keeps no copy of its own.
 */
export async function fetchThreadFilePolicy(entryId: string, signal?: AbortSignal): Promise<ThreadFilePolicy> {
  const res = await apiClient.get<DataResponse<ThreadFilePolicy>>(`${contentThreadPath(entryId)}/file-policy`, { signal });
  return res.data.data;
}

/**
 * POST /api/v1/roster/{entry_id}/threads/content/files
 *
 * Uploads one file (already compressed, if it's an image) to the content
 * thread. Returns the stored file's id plus the server-decided
 * kind/content-type — the caller collects these ids into the `file_ids` it
 * posts with the message (see PostNoteRequest.file_ids / postRosterMessage's
 * equivalent).
 */
export async function uploadThreadFile(args: UploadThreadFileArgs): Promise<ThreadFileView> {
  const res = await apiClient.post<DataResponse<ThreadFileView>>(
    `${contentThreadPath(args.entryId)}/files`,
    buildUploadForm(args),
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: toUploadProgressHandler(args.onProgress),
      signal: args.signal,
      timeout: uploadTimeoutMs(args.file.size),
    },
  );
  return res.data.data;
}

/**
 * GET /api/v1/roster/{entry_id}/threads/content/files/{file_id}
 *
 * Fetches one attached file's raw bytes. Used to build an object URL for
 * inline preview/playback — see useThreadFile. Aborting `signal` cancels
 * the download, so a viewer that closes stops pulling bytes it won't show.
 */
export async function fetchThreadFileBlob(entryId: string, fileId: string, signal?: AbortSignal): Promise<Blob> {
  const res = await apiClient.get<Blob>(
    `${contentThreadPath(entryId)}/files/${encodeURIComponent(fileId)}`,
    { responseType: 'blob', signal, timeout: THREAD_FILE_DOWNLOAD_TIMEOUT_MS },
  );
  return res.data;
}

/**
 * DELETE /api/v1/roster/{entry_id}/threads/content/files/{file_id}
 *
 * Removes one of the caller's unsent uploads, freeing its quota at once. A
 * file that is already gone counts as deleted; anything else rejects, and
 * the server's 24 h sweep is the fallback.
 */
export async function deleteThreadFile(entryId: string, fileId: string, signal?: AbortSignal): Promise<void> {
  try {
    await apiClient.delete(`${contentThreadPath(entryId)}/files/${encodeURIComponent(fileId)}`, { signal });
  } catch (err) {
    if (isNotFoundError(err)) return;
    throw err;
  }
}

/**
 * Best-effort DELETE of unsent uploads as the page goes away. A keepalive
 * `fetch` outlives the page where an axios request would be cancelled; it
 * carries the same Bearer token apiClient sends, read synchronously from the
 * cached Firebase token since nothing async survives `pagehide`. Anything
 * that doesn't land is left to the server's 24 h sweep.
 */
export function deleteThreadFilesOnExit(entryId: string, fileIds: string[]): void {
  const token = getAuthToken();
  if (!token || fileIds.length === 0) return;
  const base = `${apiClient.defaults.baseURL ?? ''}${contentThreadPath(entryId)}/files`;
  fileIds.forEach((fileId) => {
    fetch(`${base}/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(ignoreExitFailure);
  });
}

function ignoreExitFailure(): void {
  // The page is going away; the server's sweep removes what this missed.
}
